import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import Fade from '@material-ui/core/Fade';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import {
    CircularProgress,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import { StyledTableCellProps as StyledTableCell } from '../../commons/utils/TableStyles';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import * as Utils from '../../commons/utils/Utils';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '95%',
    maxWidth: '1100px',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

const PAGINATION_INIT = { page: 0, perPage: 10 };
const FILTROS_INIT = { evento: '', usuarioId: '', search: '', fechaDesde: '', fechaHasta: '' };

const EVENTO_DISPLAY = {
    CREACION:      { iconClass: 'la la-dot-circle-o' },
    GENERACION:    { iconClass: 'la la-table' },
    REGENERACION:  { iconClass: 'la la-refresh' },
    EXCLUSION:     { iconClass: 'la la-minus-circle' },
    REINCLUSION:   { iconClass: 'la la-undo' },
    EDICION_FECHA: { iconClass: 'la la-calendar-check-o' },
    PUBLICACION:   { iconClass: 'la la-bullhorn' },
    EJECUCION_TICKET_GENERADO: { iconClass: 'la la-wrench' },
    EJECUCION_SIN_SERVICIO:    { iconClass: 'la la-clock-o' },
};

/**
 * Historial cronológico de acciones del programa (VEC-3338): tabla (no timeline, para no perder
 * espacio vertical con volumen alto de entradas) con filtros por vehículo (dominio/unidad), tipo
 * de acción, usuario y rango de fechas. Las entradas posteriores a la publicación quedan marcadas
 * con un badge (datos.post_publicacion, calculado por el backend).
 *
 * Props:
 *   open       {boolean}
 *   programaId {number}
 *   onClose    {function}
 */
const ProgramasPreventivosHistorialModal = (props) => {
    const intl = useIntl();
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [paginationCount, setPaginationCount] = useState(0);
    const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
    const [filtros, setFiltros] = useState({ ...FILTROS_INIT });
    const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);

    const EVENTOS_OPCIONES = [
        { value: 'CREACION',      label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.creacion', defaultMessage: 'Creación del programa' }) },
        { value: 'GENERACION',    label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.generacion', defaultMessage: 'Generación de grilla' }) },
        { value: 'REGENERACION',  label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.regeneracion', defaultMessage: 'Regeneración de grilla' }) },
        { value: 'EXCLUSION',     label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.exclusion', defaultMessage: 'Exclusión de vehículo' }) },
        { value: 'REINCLUSION',   label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.reinclusion', defaultMessage: 'Reinclusión de vehículo' }) },
        { value: 'EDICION_FECHA', label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.edicion_fecha', defaultMessage: 'Edición de fecha sugerida' }) },
        { value: 'PUBLICACION',   label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.publicacion', defaultMessage: 'Publicación del programa' }) },
        { value: 'EJECUCION_TICKET_GENERADO', label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.ejecucion_ticket_generado', defaultMessage: 'Ticket generado automáticamente' }) },
        { value: 'EJECUCION_SIN_SERVICIO',    label: intl.formatMessage({ id: 'programasPreventivosHistorial.evento.ejecucion_sin_servicio', defaultMessage: 'Ejecución automática sin Servicio a informar' }) },
    ];

    const getEventoLabel = (evento) => {
        var opcion = EVENTOS_OPCIONES.find(function(o) { return o.value === evento; });
        return opcion ? opcion.label : evento;
    };

    const getIconClass = (evento) => (EVENTO_DISPLAY[evento] || {}).iconClass || 'la la-info-circle';

    const getDetalle = (item) => {
        var datos = item.datos || {};
        switch (item.evento) {
            case 'EXCLUSION':
                return [item.motivo, datos.comentario].filter(Boolean).join(' — ');
            case 'EDICION_FECHA':
                return datos.fecha_anterior
                    ? intl.formatMessage(
                        { id: 'programasPreventivosHistorial.detalle.edicion_fecha', defaultMessage: '{anterior} → {nueva}' },
                        { anterior: datos.fecha_anterior, nueva: datos.fecha_nueva || '—' }
                    )
                    : intl.formatMessage(
                        { id: 'programasPreventivosHistorial.detalle.edicion_fecha_sin_anterior', defaultMessage: 'Fecha sugerida: {nueva}' },
                        { nueva: datos.fecha_nueva || '—' }
                    );
            case 'GENERACION':
            case 'REGENERACION':
                return datos.vehiculos_incluidos !== undefined
                    ? intl.formatMessage(
                        { id: 'programasPreventivosHistorial.detalle.generacion', defaultMessage: '{cantidad} vehículos incluidos' },
                        { cantidad: datos.vehiculos_incluidos }
                    )
                    : '';
            default:
                return '';
        }
    };

    const handleFiltroChange = (campo, valor) => {
        setFiltros(function(prev) { return { ...prev, [campo]: valor }; });
        setPagination(function(prev) { return { ...prev, page: 0 }; });
    };

    // wasOpenRef distingue "se acaba de abrir" de "sigue abierto y cambió un filtro": evita que
    // el fetch de este mismo efecto salga con los filtros viejos (closure stale) en la misma
    // tanda de renders en la que el otro efecto todavía no terminó de resetearlos — si esa
    // respuesta vieja llega después de la nueva, pisa el resultado ya filtrado.
    // requestIdRef descarta además cualquier respuesta que no sea la del último fetch disparado
    // (mismo motivo: dos requests en vuelo, gana la que responde después, no la más reciente).
    const wasOpenRef = useRef(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!props.open || !props.programaId) {
            wasOpenRef.current = props.open;
            return;
        }

        var filtrosAUsar = filtros;
        var paginationAUsar = pagination;
        if (!wasOpenRef.current) {
            filtrosAUsar = { ...FILTROS_INIT };
            paginationAUsar = { ...PAGINATION_INIT };
            setFiltros(filtrosAUsar);
            setPagination(paginationAUsar);
        }
        wasOpenRef.current = true;

        setLoading(true);
        var requestId = ++requestIdRef.current;

        var q = '?page=' + paginationAUsar.page + '&perPage=' + paginationAUsar.perPage;
        if (filtrosAUsar.evento) q += '&evento=' + encodeURIComponent(filtrosAUsar.evento);
        if (filtrosAUsar.usuarioId) q += '&usuarioId=' + encodeURIComponent(filtrosAUsar.usuarioId);
        if (filtrosAUsar.search) q += '&search=' + encodeURIComponent(filtrosAUsar.search);
        if (filtrosAUsar.fechaDesde) q += '&fechaDesde=' + encodeURIComponent(filtrosAUsar.fechaDesde);
        if (filtrosAUsar.fechaHasta) q += '&fechaHasta=' + encodeURIComponent(filtrosAUsar.fechaHasta);

        Utils.getData('/programas-preventivos/' + props.programaId + '/historial' + q)
            .then(function(data) {
                if (requestId !== requestIdRef.current) return;
                setItems(data.data || []);
                setPaginationCount(data.pagination.count);
                setUsuariosDisponibles(data.usuarios_disponibles || []);
            })
            .catch(function() {})
            .finally(function() {
                if (requestId === requestIdRef.current) setLoading(false);
            });
    }, [props.open, props.programaId, pagination, filtros]);

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            className={classes.modal}
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={props.open}>
                <Box sx={modalStyle}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="mb-0">
                            <i className="fa fa-history"></i>{' '}
                            <FormattedMessage id="programasPreventivosHistorial.titulo" defaultMessage="Historial de cambios" />
                        </h4>
                        <button type="button" className="close" onClick={props.onClose} aria-label="close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <div className="row mb-1">
                        <div className="col-md-6 mb-2">
                            <label className="d-block mb-1 text-muted" style={{ fontSize: '12px' }}>
                                <FormattedMessage id="programasPreventivosHistorial.filtro.label_tipo_accion" defaultMessage="Tipo de acción" />
                            </label>
                            <select
                                className="form-control form-control-sm"
                                value={filtros.evento}
                                onChange={function(e) { handleFiltroChange('evento', e.target.value); }}
                            >
                                <option value="">
                                    {intl.formatMessage({ id: 'programasPreventivosHistorial.filtro.tipo_accion_todos', defaultMessage: 'Todos los tipos de acción' })}
                                </option>
                                {EVENTOS_OPCIONES.map(function(o) {
                                    return <option key={o.value} value={o.value}>{o.label}</option>;
                                })}
                            </select>
                        </div>
                        <div className="col-md-6 mb-2">
                            <label className="d-block mb-1 text-muted" style={{ fontSize: '12px' }}>
                                <FormattedMessage id="programasPreventivosHistorial.filtro.label_usuario" defaultMessage="Usuario" />
                            </label>
                            <select
                                className="form-control form-control-sm"
                                value={filtros.usuarioId}
                                onChange={function(e) { handleFiltroChange('usuarioId', e.target.value); }}
                            >
                                <option value="">
                                    {intl.formatMessage({ id: 'programasPreventivosHistorial.filtro.usuario_todos', defaultMessage: 'Todos los usuarios' })}
                                </option>
                                {usuariosDisponibles.map(function(u) {
                                    return <option key={u.id} value={u.id}>{u.nombre}</option>;
                                })}
                            </select>
                        </div>
                    </div>
                    <div className="row mb-2">
                        <div className="col-md-4 mb-2">
                            <label className="d-block mb-1 text-muted" style={{ fontSize: '12px' }}>
                                <FormattedMessage id="programasPreventivosHistorial.filtro.label_vehiculo" defaultMessage="Vehículo" />
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder={intl.formatMessage({ id: 'programasPreventivosHistorial.filtro.placeholder_vehiculo', defaultMessage: 'Dominio o unidad' })}
                                value={filtros.search}
                                onChange={function(e) { handleFiltroChange('search', e.target.value); }}
                            />
                        </div>
                        <div className="col-md-4 mb-2">
                            <label className="d-block mb-1 text-muted" style={{ fontSize: '12px' }}>
                                <FormattedMessage id="programasPreventivosHistorial.filtro.fecha_desde" defaultMessage="Desde" />
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filtros.fechaDesde}
                                onChange={function(e) { handleFiltroChange('fechaDesde', e.target.value); }}
                            />
                        </div>
                        <div className="col-md-4 mb-2">
                            <label className="d-block mb-1 text-muted" style={{ fontSize: '12px' }}>
                                <FormattedMessage id="programasPreventivosHistorial.filtro.fecha_hasta" defaultMessage="Hasta" />
                            </label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={filtros.fechaHasta}
                                onChange={function(e) { handleFiltroChange('fechaHasta', e.target.value); }}
                            />
                        </div>
                    </div>

                    <TableContainer style={{ maxHeight: '55vh' }}>
                        <Table stickyHeader aria-label="programas-preventivos-historial table" className="table-hover">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="left" style={{ width: '130px' }}>
                                        <FormattedMessage id="programasPreventivosHistorial.col_fecha" defaultMessage="Fecha" />
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="programasPreventivosHistorial.col_tipo_accion" defaultMessage="Tipo de acción" />
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="programasPreventivosHistorial.col_usuario" defaultMessage="Usuario" />
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="programasPreventivosHistorial.col_vehiculo" defaultMessage="Vehículo" />
                                    </StyledTableCell>
                                    <StyledTableCell align="left">
                                        <FormattedMessage id="programasPreventivosHistorial.col_detalle" defaultMessage="Detalle" />
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={5} align="center" style={{ padding: '40px' }}>
                                            <CircularProgress />
                                        </StyledTableCell>
                                    </TableRow>
                                ) : items.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={5} align="center" style={{ padding: '40px', color: '#999' }}>
                                            <FormattedMessage id="programasPreventivosHistorial.sin_registros" defaultMessage="No hay registros para los filtros aplicados." />
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    items.map(function(item) {
                                        var vehiculo = item.vehiculo_dominio
                                            ? item.vehiculo_dominio + (item.vehiculo_unidad ? ' — ' + item.vehiculo_unidad : '')
                                            : null;
                                        return (
                                            <TableRow key={item.id}>
                                                <StyledTableCell align="left" style={{ fontSize: '13px' }}>
                                                    {LocaleFormat.formatDateTime(item.fecha)}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" style={{ fontSize: '13px' }}>
                                                    <i className={getIconClass(item.evento)}></i> {getEventoLabel(item.evento)}
                                                    {item.post_publicacion && (
                                                        <span className="badge badge-warning ml-1" style={{ fontSize: '10px' }}>
                                                            <FormattedMessage id="programasPreventivosHistorial.badge_post_publicacion" defaultMessage="Post-publicación" />
                                                        </span>
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" style={{ fontSize: '13px' }}>
                                                    {item.usuario_nombre}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" style={{ fontSize: '13px' }}>
                                                    {vehiculo || <span className="text-muted">—</span>}
                                                </StyledTableCell>
                                                <StyledTableCell align="left" style={{ fontSize: '13px' }}>
                                                    {getDetalle(item) || <span className="text-muted">—</span>}
                                                </StyledTableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <GridPagination
                        count={paginationCount}
                        perPage={pagination.perPage}
                        page={pagination.page}
                        onPageChange={function(e, newPage) { setPagination(function(prev) { return { ...prev, page: newPage }; }); }}
                        onPerPageChange={function(e) { setPagination({ page: 0, perPage: parseInt(e.target.value, 10) }); }}
                    />
                </Box>
            </Fade>
        </Modal>
    );
};

export default ProgramasPreventivosHistorialModal;
